import dayjs from 'dayjs';

/**
 * File-name scope for an export: the business name slug (`acme-inc`), or
 * `all-businesses` for platform-wide exports.
 * @param {{ name: string }|null} business
 * @returns {string}
 */
export function scopeSlug(business) {
    if (!business) return 'all-businesses';

    const slug = business.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

    return slug || 'business';
}

/**
 * Slug used in export file names, e.g.
 * `chill-activity-report_acme-inc_2026-09-01_2026-09-30.csv`.
 * @param {object} report
 * @param {string} ext file extension without the dot
 * @returns {string}
 */
export function reportFileName(report, ext) {
    return `chill-activity-report_${scopeSlug(report.business)}_${
        report.dateFrom
    }_${report.dateTo}.${ext}`;
}

/**
 * File name for the never-logged-in export, e.g.
 * `chill-never-logged-in_acme-inc_2026-10-09.csv`.
 * @param {object} payload API response from /never-logged-in
 * @param {string} ext file extension without the dot
 * @returns {string}
 */
export function neverLoggedInFileName(payload, ext) {
    const day = dayjs(payload.generatedAt).format('YYYY-MM-DD');
    return `chill-never-logged-in_${scopeSlug(payload.business)}_${day}.${ext}`;
}

/**
 * Trigger a browser download for in-memory text content.
 * @param {string} fileName
 * @param {string} content
 * @param {string} mime
 */
export function downloadText(fileName, content, mime = 'text/plain') {
    const blob = new Blob([content], { type: `${mime};charset=utf-8` });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Give the browser a tick to start the download before revoking.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Human readable timestamp used in report headers. */
export function formatGeneratedAt(value) {
    return dayjs(value).format('MMM D, YYYY HH:mm');
}

/** `2026-09-01` → `Sep 1, 2026`. */
export function formatDay(value) {
    return dayjs(value).format('MMM D, YYYY');
}

/** `0.4` → `40%`, null → fallback. */
export function formatRate(value, fallback = 'n/a') {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return fallback;
    }
    return `${Math.round(value * 100)}%`;
}
